import { useMemo } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";

import { DecisionEnvironment } from "src/api/types";
import { findSchema, getSchemaIcon } from "src/entities/entityView/utils";
import { useEntities, useEntity, useEntitySchemas } from "src/entities/queries";
import { useEnvironment } from "src/hooks/useEnvironment";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { useWorkspaceContext } from "src/router/routerContextHooks";

export type EntitySearchItem = {
  label: string;
  key: string;
  isFetching: boolean;
};

type UseEntitySearchParams = {
  entityType: string;
  searchQuery: string;
  selectedValue: string;
};

type UseEntitySearchResult = {
  items: EntitySearchItem[];
  isLoading: boolean;
  displayName: string;
  primaryPropertyDisplayName: string | undefined;
  selectedDisplayValue: string | null;
  entityIcon: IconProp | undefined;
};

export const useEntitySearch = ({
  entityType,
  searchQuery,
  selectedValue,
}: UseEntitySearchParams): UseEntitySearchResult => {
  const [pageEnv] = useEnvironment();
  const env = pageEnv as DecisionEnvironment;

  const featureGates = useWorkspaceFeatureGates();
  const { workspace } = useWorkspaceContext();
  const { data: entitySchemas } = useEntitySchemas({
    baseUrl: workspace.base_url!,
    options: { enabled: featureGates.entitiesEnabled },
  });

  const schema = findSchema(entityType, entitySchemas);
  const primaryProperty = schema?._primary_property;
  const primaryPropertyDisplayName = primaryProperty
    ? (schema?.properties[primaryProperty]?._display_name ?? primaryProperty)
    : undefined;
  const entityIcon = schema ? getSchemaIcon(schema) : undefined;

  const userIsSearching = searchQuery.length > 0;

  const { data: entitiesByProp, isLoading: isLoadingByProp } = useEntities({
    schema: entityType,
    env,
    filters:
      userIsSearching && primaryProperty
        ? {
            [`filter[${primaryProperty}][str_contains]`]: searchQuery,
          }
        : {},
    limit: 20,
  });

  const { data: entityById, isLoading: isLoadingById } = useEntity({
    schema: entityType,
    env,
    entityId: userIsSearching ? searchQuery : undefined,
    enabled: userIsSearching,
  });

  const { data: selectedEntity } = useEntity({
    schema: entityType,
    env,
    entityId: selectedValue || undefined,
  });

  const selectedDisplayValue = useMemo(() => {
    if (!primaryProperty) return null;
    const entity =
      selectedEntity ??
      (entityById?._id === selectedValue ? entityById : undefined) ??
      entitiesByProp?.pages[0]?.entities?.find((e) => e._id === selectedValue);
    return (entity?.properties[primaryProperty] as string) || null;
  }, [
    selectedEntity,
    primaryProperty,
    selectedValue,
    entityById,
    entitiesByProp,
  ]);

  const items = useMemo(() => {
    const result: EntitySearchItem[] = [];
    const seenIds = new Set<string>();

    if (entityById) {
      seenIds.add(entityById._id);
      const ppValue = primaryProperty
        ? (entityById.properties[primaryProperty] as string)
        : undefined;
      result.push({
        label: ppValue ? `${ppValue} (${entityById._id})` : entityById._id,
        key: entityById._id,
        isFetching: false,
      });
    }

    const foundEntities = entitiesByProp?.pages[0]?.entities ?? [];
    for (const entity of foundEntities) {
      if (seenIds.has(entity._id)) continue;
      seenIds.add(entity._id);
      const ppValue = primaryProperty
        ? (entity.properties[primaryProperty] as string)
        : undefined;
      result.push({
        label: ppValue ? `${ppValue} (${entity._id})` : entity._id,
        key: entity._id,
        isFetching: false,
      });
    }

    return result;
  }, [entityById, entitiesByProp, primaryProperty]);

  const isLoading = isLoadingByProp || (userIsSearching && isLoadingById);
  const displayName =
    schema?._display_name_plural ??
    schema?._display_name_singular ??
    "Entities";

  return {
    items,
    isLoading,
    displayName,
    primaryPropertyDisplayName,
    selectedDisplayValue,
    entityIcon,
  };
};
